import { useEffect, useRef } from "react";
import { useSocket } from "../context/SocketContext";

/**
 * Custom hook to listen for real-time socket events and trigger callbacks
 *
 * @param {string} event - The event name to listen for (e.g., 'task:created')
 * @param {function} callback - Function to call when event is received
 */
export const useSocketEvent = (event, callback) => {
  const { socket } = useSocket();

  // Use a ref to store the callback to avoid re-registering the listener
  // every time the callback changes (which happens on every render with inline functions)
  const callbackRef = useRef(callback);

  // Update the ref whenever the callback changes
  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  useEffect(() => {
    if (!socket) return;

    // Create a stable handler that calls the latest callback from the ref
    const handler = (data) => {
      if (callbackRef.current) {
        callbackRef.current(data);
      }
    };

    socket.on(event, handler);

    return () => {
      socket.off(event, handler);
    };
  }, [socket, event]); // Note: callback is NOT a dependency anymore
};

/**
 * Hook to automatically join/leave project rooms and listen for project-related updates
 *
 * @param {string} projectId - The project ID to join
 * @param {object} callbacks - Object with event callbacks
 * @example
 * useProjectRealtime(projectId, {
 * });
 */
export const useProjectRealtime = (projectId, callbacks = {}) => {
  const { joinProject, leaveProject } = useSocket();

  useEffect(() => {
    if (!projectId) return;

    // Join the project room
    joinProject(projectId);

    return () => {
      // Leave the project room on cleanup
      leaveProject(projectId);
    };
  }, [projectId, joinProject, leaveProject]);

  // Task events
  useSocketEvent("task:created", callbacks.onTaskCreated || (() => {}));
  useSocketEvent("task:updated", callbacks.onTaskUpdated || (() => {}));
  useSocketEvent("task:deleted", callbacks.onTaskDeleted || (() => {}));
  useSocketEvent("task:reordered", callbacks.onTaskReordered || (() => {}));
  useSocketEvent(
    "task:bulk-deleted",
    callbacks.onTaskBulkDeleted || (() => {}),
  );

  // Section events
  useSocketEvent("section:created", callbacks.onSectionCreated || (() => {}));
  useSocketEvent("section:updated", callbacks.onSectionUpdated || (() => {}));
  useSocketEvent("section:deleted", callbacks.onSectionDeleted || (() => {}));

  // Board events
  useSocketEvent("board:created", callbacks.onBoardCreated || (() => {}));
  useSocketEvent("board:updated", callbacks.onBoardUpdated || (() => {}));
  useSocketEvent("board:deleted", callbacks.onBoardDeleted || (() => {}));
  useSocketEvent("board:reordered", callbacks.onBoardsReordered || (() => {}));

  // Note events
  useSocketEvent("note:created", callbacks.onNoteCreated || (() => {}));
  useSocketEvent("note:updated", callbacks.onNoteUpdated || (() => {}));
  useSocketEvent("note:deleted", callbacks.onNoteDeleted || (() => {}));
  useSocketEvent("note:reordered", callbacks.onNoteReordered || (() => {}));
  useSocketEvent(
    "note:bulk-deleted",
    callbacks.onNoteBulkDeleted || (() => {}),
  );

  // NoteGroup events
  useSocketEvent(
    "noteGroup:created",
    callbacks.onNoteGroupCreated || (() => {}),
  );
  useSocketEvent(
    "noteGroup:updated",
    callbacks.onNoteGroupUpdated || (() => {}),
  );
  useSocketEvent(
    "noteGroup:deleted",
    callbacks.onNoteGroupDeleted || (() => {}),
  );
};

/**
 * Hook to automatically join/leave organization rooms and listen for org-related updates
 *
 * @param {string} organizationId - The organization ID to join
 * @param {object} callbacks - Object with event callbacks
 * @example
 * useOrganizationRealtime(organizationId, {
 * });
 */
export const useOrganizationRealtime = (organizationId, callbacks = {}) => {
  const { joinOrganization, leaveOrganization } = useSocket();

  useEffect(() => {
    if (!organizationId) return;

    // Join the organization room
    joinOrganization(organizationId);

    return () => {
      // Leave the organization room on cleanup
      leaveOrganization(organizationId);
    };
  }, [organizationId, joinOrganization, leaveOrganization]);

  // Project events
  useSocketEvent("project:created", callbacks.onProjectCreated || (() => {}));
  useSocketEvent("project:updated", callbacks.onProjectUpdated || (() => {}));
  useSocketEvent("project:deleted", callbacks.onProjectDeleted || (() => {}));

  // Organization events
  useSocketEvent(
    "organization:created",
    callbacks.onOrganizationCreated || (() => {}),
  );
  useSocketEvent(
    "organization:updated",
    callbacks.onOrganizationUpdated || (() => {}),
  );

  // Team events
  useSocketEvent("team:created", callbacks.onTeamCreated || (() => {}));
  useSocketEvent("team:updated", callbacks.onTeamUpdated || (() => {}));
  useSocketEvent("team:deleted", callbacks.onTeamDeleted || (() => {}));

  // User/Member events
  useSocketEvent(
    "user:role-updated",
    callbacks.onUserRoleUpdated || (() => {}),
  );
  useSocketEvent("user:removed", callbacks.onUserRemoved || (() => {}));
  useSocketEvent("user:updated", callbacks.onUserUpdated || (() => {}));
};

/**
 * Hook to automatically join/leave user rooms and listen for user-specific updates
 * Used for personal data like todolists
 *
 * @param {string} userId - The user ID to join
 * @param {object} callbacks - Object with event callbacks
 * @example
 * useUserRealtime(userId, {
 *   onTodoListCreated: () => {},
 *   onTodoListUpdated: () => {},
 *   onTodoListDeleted: () => {},
 * });
 */
export const useUserRealtime = (userId, callbacks = {}) => {
  const { joinUser, leaveUser } = useSocket();

  useEffect(() => {
    if (!userId) return;

    // Join the user room
    joinUser(userId);

    return () => {
      // Leave the user room on cleanup
      leaveUser(userId);
    };
  }, [userId, joinUser, leaveUser]);

  // TodoList events
  useSocketEvent("todoList:created", callbacks.onTodoListCreated || (() => {}));
  useSocketEvent("todoList:updated", callbacks.onTodoListUpdated || (() => {}));
  useSocketEvent("todoList:deleted", callbacks.onTodoListDeleted || (() => {}));
  useSocketEvent(
    "todoList:reordered",
    callbacks.onTodoListReordered || (() => {}),
  );
};
